import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Form, Spinner, Alert } from 'react-bootstrap';
import { 
  FaSearch, 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaShieldAlt, 
  FaCity, 
  FaLayerGroup,
  FaChevronLeft,
  FaChevronRight,
  FaTimes
} from 'react-icons/fa';
import { API_CONFIG } from '../../config/api';
import { serviceCentersData } from '../../data/serviceCentersData';
import './ServiceCentre.css';

const ServiceCentre = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('ALL');
  const [selectedCity, setSelectedCity] = useState('ALL');
  const [centres, setCentres] = useState(serviceCentersData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  // Fetch all service centers from API with local fallback
  useEffect(() => {
    setLoading(true);
    fetch(`${API_CONFIG.SERVICE_CENTERS_URL}`)
      .then(response => {
        if (!response.ok) throw new Error('API server unavailable');
        return response.json();
      })
      .then(data => {
        if (data && Array.isArray(data.data) && data.data.length > 0) {
          setCentres(data.data);
        } else {
          setCentres(serviceCentersData);
        }
        setLoading(false);
      })
      .catch((err) => {
        // Fallback to rich embedded dataset
        setCentres(serviceCentersData);
        setError(null);
        setLoading(false);
      });
  }, []);

  // Extract unique sorted states
  const statesList = useMemo(() => {
    const states = new Set();
    centres.forEach(c => {
      if (c.state) states.add(c.state.trim());
    });
    return Array.from(states).sort();
  }, [centres]);

  // Extract unique sorted cities based on selected state
  const citiesList = useMemo(() => {
    const cities = new Set();
    centres.forEach(c => {
      if (c.city) {
        if (selectedState === 'ALL' || c.state === selectedState) {
          cities.add(c.city.trim());
        }
      }
    });
    return Array.from(cities).sort();
  }, [centres, selectedState]);

  // Filter service centers
  const filteredCentres = useMemo(() => {
    let result = [...centres];

    // State filter
    if (selectedState !== 'ALL') {
      result = result.filter(c => c.state === selectedState);
    }

    // City filter
    if (selectedCity !== 'ALL') {
      result = result.filter(c => c.city === selectedCity);
    }

    // Search query filter (matches name, address, city, state, phone, pincode)
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.address && c.address.toLowerCase().includes(q)) ||
        (c.city && c.city.toLowerCase().includes(q)) ||
        (c.state && c.state.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        (c.pincode && c.pincode.toLowerCase().includes(q))
      );
    }

    return result;
  }, [centres, selectedState, selectedCity, searchTerm]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedState, selectedCity]);

  // Pagination slice
  const totalPages = Math.ceil(filteredCentres.length / itemsPerPage);
  const currentCentres = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCentres.slice(start, start + itemsPerPage);
  }, [filteredCentres, currentPage]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedState('ALL');
    setSelectedCity('ALL');
    setCurrentPage(1);
  };

  return (
    <div className="service-centre-page-root">
      {/* Hero Section */}
      <section className="sc-hero-section">
        <Container>
          <div className="sc-hero-content text-center">
            <span className="sc-eyebrow">
              <FaShieldAlt className="me-2" /> Nationwide Customer Support
            </span>
            <h1 className="sc-main-title">
              Authorized Service Centers
            </h1>
            <p className="sc-subtitle">
              Locate trusted service and repair experts near you across India. Genuine parts, certified technicians, and prompt customer assistance.
            </p>

            {/* Quick Stat Badges */}
            <div className="sc-stats-bar">
              <div className="sc-stat-pill">
                <strong>600+</strong> Authorized Centers
              </div>
              <div className="sc-stat-pill">
                <strong>28</strong> States & UTs
              </div>
              <div className="sc-stat-pill">
                <strong>450+</strong> Cities Covered
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Search & Filter Toolbar */}
      <section className="sc-toolbar-section">
        <Container>
          <div className="sc-filters-card">
            <Row className="g-3 align-items-center">
              {/* Search Box */}
              <Col lg={5} md={12}>
                <div className="sc-search-box">
                  <FaSearch className="sc-search-icon" />
                  <input
                    type="text"
                    placeholder="Search by city, center name, PIN code, or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="sc-search-input"
                    aria-label="Search service centers"
                  />
                  {searchTerm && (
                    <button 
                      className="sc-clear-btn" 
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </Col>

              {/* State Filter */}
              <Col lg={3} sm={6}>
                <div className="sc-select-wrapper">
                  <Form.Select 
                    value={selectedState} 
                    onChange={(e) => {
                      setSelectedState(e.target.value);
                      setSelectedCity('ALL');
                    }}
                    className="sc-select-input"
                    aria-label="Filter by State"
                  >
                    <option value="ALL">All States ({statesList.length})</option>
                    {statesList.map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </Form.Select>
                </div>
              </Col>

              {/* City Filter */}
              <Col lg={3} sm={6}>
                <div className="sc-select-wrapper">
                  <Form.Select 
                    value={selectedCity} 
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="sc-select-input"
                    aria-label="Filter by City"
                  >
                    <option value="ALL">All Cities ({citiesList.length})</option>
                    {citiesList.map(ct => (
                      <option key={ct} value={ct}>{ct}</option>
                    ))}
                  </Form.Select>
                </div>
              </Col>

              {/* Reset Button */}
              <Col lg={1} xs={12} className="text-center">
                <button 
                  className="sc-reset-btn"
                  onClick={handleResetFilters}
                  title="Reset all filters"
                >
                  Reset
                </button>
              </Col>
            </Row>

            {/* Results Count Bar */}
            <div className="sc-results-meta mt-3">
              <div className="d-flex align-items-center gap-2">
                <FaLayerGroup className="text-primary" />
                <span>
                  Found <strong>{filteredCentres.length}</strong> service centers
                  {selectedState !== 'ALL' && ` in ${selectedState}`}
                  {selectedCity !== 'ALL' && `, ${selectedCity}`}
                </span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Service Centers Grid */}
      <section className="sc-grid-section">
        <Container>
          {loading ? (
            <div className="sc-loading-state">
              <Spinner animation="border" className="sc-spinner" />
              <p>Loading authorized service centers...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="text-center">{error}</Alert>
          ) : filteredCentres.length === 0 ? (
            <div className="sc-empty-state">
              <h3>No Service Centers Found</h3>
              <p>No service centers match your search criteria. Try selecting "All States" or clearing your query.</p>
              <button 
                className="apple-btn apple-btn-primary mt-3"
                onClick={handleResetFilters}
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <Row className="g-4">
                {currentCentres.map((centre) => (
                  <Col key={centre.id} lg={4} md={6} sm={12}>
                    <div className="sc-center-card">
                      {/* Top Badge Row */}
                      <div className="sc-card-badge-row">
                        <span className="sc-city-tag">
                          <FaCity className="me-1" /> {centre.city || 'City'}
                        </span>
                        <span className="sc-state-tag">{centre.state || 'India'}</span>
                      </div>

                      {/* Center Name */}
                      <h3 className="sc-card-name">{centre.name}</h3>

                      {/* Address */}
                      <div className="sc-info-row address-row">
                        <FaMapMarkerAlt className="sc-info-icon marker-icon" />
                        <span className="sc-address-text">{centre.address}</span>
                      </div>

                      {/* Phone Numbers */}
                      {centre.phone && (
                        <div className="sc-info-row phone-row">
                          <FaPhoneAlt className="sc-info-icon phone-icon" />
                          <div className="sc-phone-numbers">
                            {centre.phone.split('/').map((ph, i) => {
                              const cleanPh = ph.trim().replace(/[^0-9+]/g, '');
                              return (
                                <a 
                                  key={i} 
                                  href={`tel:${cleanPh}`} 
                                  className="sc-phone-link"
                                >
                                  {ph.trim()}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Email */}
                      {centre.email && (
                        <div className="sc-info-row email-row">
                          <FaEnvelope className="sc-info-icon email-icon" />
                          <a 
                            href={`mailto:${centre.email}`} 
                            className="sc-email-link"
                          >
                            {centre.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="sc-pagination-wrapper">
                  <button
                    className="sc-page-nav-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous Page"
                  >
                    <FaChevronLeft className="me-1" /> Prev
                  </button>

                  <div className="sc-page-indicator">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </div>

                  <button
                    className="sc-page-nav-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    aria-label="Next Page"
                  >
                    Next <FaChevronRight className="ms-1" />
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default ServiceCentre;
